// Angular imports
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// Rxjs import
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// Service imports
import { MPCacheRegistrationService } from '../../../_services/shared-services/mp-cache-registration-service';

// Constant imports
import { URLConstants } from '../../../_constants/url-constants';
import { CacheDuration } from '../../../_constants/cache-duration';

// Model imports
import { OwnerSearchResultDto } from '../../_models/owner-search-result-dto';
import { SearchParametersDto } from '../../_models/search-parameters-dto';
import { BaseParameterDto } from '../../../_models/base-parameter-dto';
import { SearchTypeResultDto } from '../../_models/search-type-result-dto';
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { SearchArchitecturalApplicationsResultDto } from '../../_models/search-architectural-applications-result-dto';
import { InvoiceSearchResultDto } from '../../_models/invoice-search-result-dto';
import { ProjectTaskSearchResultDto } from '../../_models/project-tasks-search-result-dto';
import { WorkOrderSearchResultDto } from '../../_models/work-order-search-result-dto';
import { VendorSearchResultDto } from '../../_models/vendors-search-result-dto';
import { IndividualHomeOwnerSearchByNameResultDto } from '../../_models/individual-home-owner-search-by-name-result-dto';
import { CompanyHomeOwnerSearchByNameResultDto } from '../../_models/company-home-owner-search-by-name-result-dto';
import { SearchBuilderDeclarantParameterDto } from '../../_models/search-builder-declarant-parameter-dto';
import { SearchBuilderDeclarantByNameResultDto } from '../../_models/search-builder-declarant-by-name-result-dto';
import { VendorDisbursementSearchResultDto } from '../../_models/vendor-disbursement-search-result-dto';
import { SearchViolationNoticesResultDto } from '../../_models/search-violation-notices-result-dto';
import { BaseProcessNotesResultDto } from '../../../management-common/_models/notes/base-process-notes-result-dto';
import { ProjectTaskHistoryResultDto } from '../../../project-task-module/_models/project-task-history-result-dto';
import { WorkOrderHistoryResultDto } from '../../_models/work-order-history-result-dto';
import { BaseSearchHistoryParametersDto } from '../../_models/base-search-history-parameters-dto';
import { InvoiceHistoryResultDto } from '../../_models/invoice-history-result-dto';
import { BaseSearchNotesParametersDto } from '../../_models/base-search-notes-parameters-dto';
import { BaseSearchAddNotesParametersDto } from '../../_models/base-search-add-notes-parameters-dto';
import { BaseSearchDocumentsParametersDto } from '../../_models/base-search-documents-parameters-dto';

@Injectable({
    providedIn: 'root'
})
export class SearchService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getCommunityList(baseParams: BaseParameterDto): Observable<CommunitySelectorResultDto[]> {
        const url = `${URLConstants.ccCommunityApiBaseUrl}selector${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get<CommunitySelectorResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(CommunitySelectorResultDto, response)));
    }

    getSearchTypes(baseParams: BaseParameterDto): Observable<SearchTypeResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/types${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<SearchTypeResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(SearchTypeResultDto, response)));
    }

    searchCurrentOwners(baseParams: SearchParametersDto): Observable<OwnerSearchResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/currentowners${baseParams.getQueryString()}`;
        return this.httpClient
            .get<OwnerSearchResultDto[]>(url)
            .pipe(map((response) => plainToClass(OwnerSearchResultDto, response)));
    }

    searchAllOwners(baseParams: SearchParametersDto): Observable<OwnerSearchResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/allowners${baseParams.getQueryString()}`;
        return this.httpClient
            .get<OwnerSearchResultDto[]>(url)
            .pipe(map((response) => plainToClass(OwnerSearchResultDto, response)));
    }

    searchArchitecturalApplications(
        baseParams: SearchParametersDto
    ): Observable<SearchArchitecturalApplicationsResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/acc${baseParams.getQueryString()}`;
        return this.httpClient
            .get<SearchArchitecturalApplicationsResultDto[]>(url)
            .pipe(map((response) => plainToClass(SearchArchitecturalApplicationsResultDto, response)));
    }

    searchInvoiceWorkflow(baseParams: SearchParametersDto): Observable<InvoiceSearchResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/invoices${baseParams.getQueryString()}`;
        return this.httpClient
            .get<InvoiceSearchResultDto[]>(url)
            .pipe(map((response) => plainToClass(InvoiceSearchResultDto, response)));
    }

    searchProjectTasks(baseParams: SearchParametersDto): Observable<ProjectTaskSearchResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/projecttasks${baseParams.getQueryString()}`;
        return this.httpClient
            .get<ProjectTaskSearchResultDto[]>(url)
            .pipe(map((response) => plainToClass(ProjectTaskSearchResultDto, response)));
    }

    searchWorkOrders(baseParams: SearchParametersDto): Observable<WorkOrderSearchResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/workorders${baseParams.getQueryString()}`;
        return this.httpClient
            .get<WorkOrderSearchResultDto[]>(url)
            .pipe(map((response) => plainToClass(WorkOrderSearchResultDto, response)));
    }

    searchViolationNotices(baseParams: SearchParametersDto): Observable<SearchViolationNoticesResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/drv${baseParams.getQueryString()}`;
        return this.httpClient
            .get<SearchViolationNoticesResultDto[]>(url)
            .pipe(map((response) => plainToClass(SearchViolationNoticesResultDto, response)));
    }

    searchVendorDisbursements(baseParams: SearchParametersDto): Observable<VendorDisbursementSearchResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/vendordisbursements${baseParams.getQueryString()}`;
        return this.httpClient
            .get<VendorDisbursementSearchResultDto[]>(url)
            .pipe(map((response: any[]) => plainToClass(VendorDisbursementSearchResultDto, response)));
    }

    searchVendors(baseParams: SearchParametersDto): Observable<VendorSearchResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/vendors${baseParams.getQueryString()}`;
        return this.httpClient
            .get<VendorSearchResultDto[]>(url)
            .pipe(map((response) => plainToClass(VendorSearchResultDto, response)));
    }

    searchCurrentIndividualOwnerByName(
        params: SearchParametersDto
    ): Observable<IndividualHomeOwnerSearchByNameResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/owner/current/name${params.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(IndividualHomeOwnerSearchByNameResultDto, response)));
    }

    searchNonCurrentIndividualOwnerByName(
        params: SearchParametersDto
    ): Observable<IndividualHomeOwnerSearchByNameResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/owner/noncurrent/name${params.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(IndividualHomeOwnerSearchByNameResultDto, response)));
    }

    searchAllIndividualOwnerByName(
        params: SearchParametersDto
    ): Observable<IndividualHomeOwnerSearchByNameResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/owner/all/name${params.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(IndividualHomeOwnerSearchByNameResultDto, response)));
    }

    searchCurrentCompanyOwnerByName(params: SearchParametersDto): Observable<CompanyHomeOwnerSearchByNameResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/companyowner/current/name${params.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(CompanyHomeOwnerSearchByNameResultDto, response)));
    }

    searchNonCurrentCompanyOwnerByName(
        params: SearchParametersDto
    ): Observable<CompanyHomeOwnerSearchByNameResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/companyowner/noncurrent/name${params.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(CompanyHomeOwnerSearchByNameResultDto, response)));
    }

    searchAllCompanyOwnerByName(params: SearchParametersDto): Observable<CompanyHomeOwnerSearchByNameResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/companyowner/all/name${params.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(CompanyHomeOwnerSearchByNameResultDto, response)));
    }

    searchBranchBuilderDeclarant(
        ownerTypeText: string,
        param: SearchBuilderDeclarantParameterDto
    ): Observable<SearchBuilderDeclarantByNameResultDto[]> {
        if (ownerTypeText === 'Builder') {
            return this.searchBranchBuilder(param);
        } else if (ownerTypeText === 'Declarant') {
            return this.searchBranchDeclarant(param);
        }
        return new Observable((ob) => ob.next([]));
    }

    searchBranchBuilder(
        param: SearchBuilderDeclarantParameterDto
    ): Observable<SearchBuilderDeclarantByNameResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/builder/name`;
        return this.httpClient
            .post(url, param)
            .pipe(map((response: any[]) => plainToClass(SearchBuilderDeclarantByNameResultDto, response)));
    }

    searchBranchDeclarant(
        param: SearchBuilderDeclarantParameterDto
    ): Observable<SearchBuilderDeclarantByNameResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/declarant/name`;
        return this.httpClient
            .post(url, param)
            .pipe(map((response: any[]) => plainToClass(SearchBuilderDeclarantByNameResultDto, response)));
    }

    getProjectTaskNotes(param: BaseSearchNotesParametersDto): Observable<BaseProcessNotesResultDto> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/projecttask/notes${param.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(
                map((responseData: BaseProcessNotesResultDto) => plainToClass(BaseProcessNotesResultDto, responseData))
            );
    }

    addProjectTaskNotes(param: BaseSearchAddNotesParametersDto): Observable<any> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/projecttask/notes`;
        return this.httpClient.post(url, classToPlain(param));
    }

    getProjectTaskHistory(param: BaseSearchHistoryParametersDto): Observable<ProjectTaskHistoryResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/projecttask/history${param.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(
                map((responseData: ProjectTaskHistoryResultDto[]) =>
                    plainToClass(ProjectTaskHistoryResultDto, responseData)
                )
            );
    }

    getWorkOrderNotes(param: BaseSearchNotesParametersDto): Observable<BaseProcessNotesResultDto> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/workorder/notes${param.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(
                map((responseData: BaseProcessNotesResultDto) => plainToClass(BaseProcessNotesResultDto, responseData))
            );
    }

    addWorkOrderNotes(param: BaseSearchAddNotesParametersDto): Observable<any> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/workorder/notes`;
        return this.httpClient.post(url, classToPlain(param));
    }

    getWorkOrderHistory(param: BaseSearchHistoryParametersDto): Observable<WorkOrderHistoryResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/workorder/history${param.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(
                map((responseData: WorkOrderHistoryResultDto[]) =>
                    plainToClass(WorkOrderHistoryResultDto, responseData)
                )
            );
    }

    getInvoiceHistory(param: BaseSearchHistoryParametersDto): Observable<InvoiceHistoryResultDto[]> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/invoice/history${param.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(
                map((responseData: InvoiceHistoryResultDto[]) => plainToClass(InvoiceHistoryResultDto, responseData))
            );
    }

    getInvoicesNotes(param: BaseSearchNotesParametersDto): Observable<BaseProcessNotesResultDto> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/invoice/notes${param.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(
                map((responseData: BaseProcessNotesResultDto) => plainToClass(BaseProcessNotesResultDto, responseData))
            );
    }

    addInvoicesNotes(param: BaseSearchAddNotesParametersDto): Observable<any> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/invoice/notes`;
        return this.httpClient.post(url, classToPlain(param));
    }

    getInvoiceDocumentFile(param: BaseSearchDocumentsParametersDto): Observable<Blob> {
        const url = `${URLConstants.ccSeachBaseApiUrl}/invoice/download${param.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }
}
